/**
 * The hostile-fire adapter (ACT3-BIBLE §9's second engine touch). Act III's
 * Understudy fires the player's *actual* weapons back — the real `Weapon`
 * subclasses, running their real code. Those classes call `pool.fire(config)`
 * against a ProjectilePool of player `Projectile`s; the boss side needs
 * `BossBullet`s out of a BulletPool instead.
 *
 * Rather than teach every weapon about bosses (the wrong direction of
 * dependency, and a change to seven files that would be paid for by one boss),
 * this wraps a BulletPool in something shaped exactly like a ProjectilePool:
 * one `fire()` that translates a player-projectile config into a boss-bullet
 * one. The weapons never learn anything.
 *
 * Two translations are load-bearing:
 *
 *  - **Damage scale.** A late-game build's output would delete the player
 *    instantly if handed back at face value, so every mirrored shot is scaled.
 *    This is the Understudy's only safety valve and the reason the bible put it
 *    at boss #2 rather than #5.
 *  - **Lifetime clamp.** Standing weapons (Orbit Ward, and the Halo/Nova Ward
 *    fusions) spawn effectively-immortal orbiting shards. Those fields mean
 *    nothing to a BossBullet, so uncapped they'd become a permanent, motionless
 *    minefield at the boss's feet. Clamped, they read as a brief ring of motes
 *    around the boss — the ward, seen from the other side — and expire.
 */
const MAX_MIRRORED_LIFETIME = 1.4; // seconds

export function createHostileFireAdapter(bulletPool, { damageScale = 1, color = null } = {}) {
  return {
    // ProjectilePool exposes this for the Piercing Gale pet; the weapons never
    // read it, but keeping the shape identical avoids a surprise later.
    bonusHits: 0,
    fire(config) {
      return bulletPool.fire({
        x: config.x,
        y: config.y,
        // Stationary player effects (Nova Pulse's rings, the Umbral Lantern's
        // beam segments) carry no velocity at all — they translate cleanly into
        // stationary boss bullets, which is exactly what they should be.
        vx: config.vx ?? 0,
        vy: config.vy ?? 0,
        radius: config.radius ?? 2.5,
        damage: (config.damage ?? 0) * damageScale,
        color: color ?? config.color,
        lifetime: Math.min(config.lifetime ?? 1, MAX_MIRRORED_LIFETIME),
        homing: config.homing ?? false,
        turnRate: config.turnRate ?? 2,
        // A homing player projectile aims at a boss; mirrored, it aims at the
        // player. The caller supplies that target through the config it builds.
        target: config.target ?? null,
      });
    },
  };
}
